import React, { useCallback, useEffect, useState } from 'react';
import { TTreeItemDataViewModel } from '../../services/models/CourseViewModels';
import { useStore } from '../../store/store';
import { runInAction } from 'mobx';

interface UseEditablePageTitleParams {
  data: TTreeItemDataViewModel;
  coursePageID?: string;
  fontSize?: number;
  onFocus?: (e: any) => void;
}

export default function useEditablePageTitle({ data, coursePageID, fontSize, onFocus }: UseEditablePageTitleParams) {
  const { propertyStore, courseStore } = useStore();
  const { setPropertyByValueID } = propertyStore;
  const { newPageCoursePageID, resetNewPageCoursePageID, updatePagePropertiesTitle } = courseStore;

  const [editTitleMode, setEditTitleMode] = useState(false);
  const [isTitleTruncated, setIsTitleTruncated] = useState(false);
  const [titleMeasureNode, setTitleMeasureNode] = useState<HTMLElement | null>(null);

  const titleMeasureRef = useCallback((node: HTMLElement | null) => {
    setTitleMeasureNode(node);
  }, []);

  const updateTitleTruncation = useCallback(() => {
    if (!titleMeasureNode) {
      setIsTitleTruncated(false);
      return;
    }

    setIsTitleTruncated(titleMeasureNode.scrollWidth > titleMeasureNode.clientWidth);
  }, [titleMeasureNode]);

  const onTitleChange = useCallback(
    (newTitle: string) => {
      runInAction(() => {
        data.title.value = newTitle;
      });
      updatePagePropertiesTitle(newTitle);
    },
    [data, updatePagePropertiesTitle]
  );

  const updateTitle = useCallback(() => {
    if (editTitleMode) {
      setPropertyByValueID(data.title.propertyValueID, data.title.value);
    }
    setEditTitleMode(false);
    resetNewPageCoursePageID();
  }, [data, editTitleMode, resetNewPageCoursePageID, setPropertyByValueID]);

  const handleOnKeyDown = useCallback(
    (e: any) => {
      if (editTitleMode && (e.key === 'Enter' || e.keyCode === 13)) {
        updateTitle();
      }
    },
    [editTitleMode, updateTitle]
  );

  const handleOnClick = useCallback((e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setEditTitleMode(true);
  }, []);

  const handleOnDoubleClick = useCallback((e: any) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleTextFieldClick = useCallback((event: React.MouseEvent<HTMLInputElement>) => {
    event.stopPropagation();
  }, []);

  const handleOnFocus = useCallback(
    (e: any) => {
      if (onFocus) {
        onFocus(e);
      }
    },
    [onFocus]
  );

  useEffect(() => {
    setEditTitleMode(coursePageID === newPageCoursePageID ? true : false);
  }, []);

  useEffect(() => {
    updateTitleTruncation();
  }, [updateTitleTruncation, data.title?.value, fontSize]);

  useEffect(() => {
    if (!titleMeasureNode || typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver(() => updateTitleTruncation());
    observer.observe(titleMeasureNode);

    return () => {
      observer.disconnect();
    };
  }, [titleMeasureNode, updateTitleTruncation]);

  useEffect(() => {
    window.addEventListener('resize', updateTitleTruncation);
    return () => {
      window.removeEventListener('resize', updateTitleTruncation);
    };
  }, [updateTitleTruncation]);

  return {
    editTitleMode,
    isTitleTruncated,
    titleMeasureRef,
    onTitleChange,
    updateTitle,
    handleOnKeyDown,
    handleOnClick,
    handleOnDoubleClick,
    handleTextFieldClick,
    handleOnFocus,
  };
}
